/**
 * SADARayon Phase 2/3 — Workflow Référent rayon + Validation Manager Stock.
 *
 * Affiché entre la clôture OP2 et la clôture finale, uniquement si l'inventaire
 * contient au moins un écart. Deux états :
 *   1. status === 'commentaire_referent'
 *      → le responsable du rayon poste un commentaire pour justifier les écarts
 *   2. status === 'validation_manager_requise'
 *      → Cyrille (manager/admin) valide ou refuse l'inventaire
 *
 * Non monté pour les autres statuts.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquareText, ShieldCheck, ShieldX, Loader2, Pencil } from 'lucide-react';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const auth = () => {
  const t = localStorage.getItem('dermascan_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtFcfa = (n) => `${Math.abs(Number(n || 0)).toLocaleString('fr-FR')} FCFA`;

const InventoryReferentValidationCard = ({
  inventory,
  lines = [],
  currentUser,
  canManagePhases,
  onTransition,
}) => {
  // Hooks MUST be declared before any early return to respect React Hooks rules.
  const [refComment, setRefComment] = useState('');
  const [refLoading, setRefLoading] = useState(false);
  const [decision, setDecision] = useState(null); // 'approve' | 'reject' | null
  const [mgrComment, setMgrComment] = useState('');
  const [mgrLoading, setMgrLoading] = useState(false);
  const [editingLineId, setEditingLineId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingComment, setEditingComment] = useState('');
  const [editingLoading, setEditingLoading] = useState(false);

  const status = inventory?.status;
  const isReferentPhase = status === 'commentaire_referent';
  const isManagerPhase = status === 'validation_manager_requise';
  if (!isReferentPhase && !isManagerPhase) return null;

  const myId = currentUser?.id;
  const isResp = myId && myId === inventory?.responsable_id;
  const canActAsResp = isResp || canManagePhases;
  const canActAsManager = canManagePhases;

  const ecartLines = (lines || []).filter((l) => l.ecart !== null && l.ecart !== 0);

  const submitReferentComment = async () => {
    if (refComment.trim().length < 10) {
      toast.error('Commentaire trop court (10 caractères minimum).');
      return;
    }
    setRefLoading(true);
    try {
      await axios.post(
        `${API}/inventory/inventories/${inventory.id}/referent-comment`,
        { comment: refComment.trim() },
        { headers: auth() },
      );
      toast.success('Justification envoyée. En attente de validation manager.');
      onTransition && onTransition();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi commentaire.');
    } finally {
      setRefLoading(false);
    }
  };

  const submitManagerValidation = async (which) => {
    if (which === 'reject' && mgrComment.trim().length < 10) {
      toast.error('Un commentaire détaillé est requis pour refuser (≥ 10 caractères).');
      return;
    }
    setMgrLoading(true);
    try {
      const res = await axios.post(
        `${API}/inventory/inventories/${inventory.id}/manager-validation`,
        { decision: which, comment: mgrComment.trim() || null },
        { headers: auth() },
      );
      if (which === 'approve') {
        toast.success(`Inventaire validé · statut ${res.data?.status}.`);
      } else {
        toast.success('Inventaire renvoyé à OP2 pour re-comptage.');
      }
      onTransition && onTransition();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur validation.');
    } finally {
      setMgrLoading(false);
    }
  };

  const submitLineCorrection = async (lineId) => {
    const val = parseInt(editingValue, 10);
    if (Number.isNaN(val) || val < 0) {
      toast.error('Stock physique invalide (entier ≥ 0).');
      return;
    }
    setEditingLoading(true);
    try {
      await axios.put(
        `${API}/inventory/inventories/${inventory.id}/lines/${lineId}/manager-correction`,
        { stock_physique: val, comment: editingComment.trim() || null },
        { headers: auth() },
      );
      toast.success('Ligne corrigée.');
      setEditingLineId(null);
      setEditingValue('');
      setEditingComment('');
      onTransition && onTransition();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur correction ligne.');
    } finally {
      setEditingLoading(false);
    }
  };

  // ── Phase 1 : commentaire référent ────────────────────────────────────
  if (isReferentPhase) {
    return (
      <div
        className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3"
        data-testid="inv-referent-card"
      >
        <div className="flex items-start gap-2">
          <MessageSquareText className="w-5 h-5 text-amber-700 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-amber-900">
              Justification des écarts requise
            </h3>
            <p className="text-xs text-amber-800 mt-0.5">
              {ecartLines.length} ligne(s) avec écart. Le responsable du rayon
              <strong> {inventory.responsable_name || '—'} </strong>
              doit justifier avant validation par le Manager Stock.
            </p>
          </div>
        </div>

        {canActAsResp ? (
          <>
            <Textarea
              value={refComment}
              onChange={(e) => setRefComment(e.target.value)}
              placeholder="Expliquez ce qui a causé les écarts détectés (casse, péremption, vol, erreur de saisie...)."
              maxLength={2000}
              rows={4}
              className="bg-white border-amber-300 focus:ring-amber-500"
              data-testid="inv-referent-comment-input"
              disabled={refLoading}
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-amber-700">
                {refComment.length}/2000 · minimum 10 caractères
              </span>
              <Button
                size="sm"
                onClick={submitReferentComment}
                disabled={refLoading || refComment.trim().length < 10}
                className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
                data-testid="inv-referent-submit-btn"
              >
                {refLoading && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                Envoyer la justification
              </Button>
            </div>
          </>
        ) : (
          <div className="text-sm text-amber-800 italic" data-testid="inv-referent-waiting">
            En attente que <strong>{inventory.responsable_name || 'le responsable'}</strong> poste sa justification.
          </div>
        )}
      </div>
    );
  }

  // ── Phase 2 : validation manager ──────────────────────────────────────
  return (
    <div
      className="rounded-lg border border-indigo-300 bg-indigo-50 p-4 space-y-3"
      data-testid="inv-manager-validation-card"
    >
      <div className="flex items-start gap-2">
        <ShieldCheck className="w-5 h-5 text-indigo-700 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-indigo-900">
            Validation Manager Stock requise
          </h3>
          <p className="text-xs text-indigo-800 mt-0.5">
            {ecartLines.length} ligne(s) avec écart · justifiées par
            <strong> {inventory.referent_comment_by_name || '—'} </strong>
          </p>
        </div>
      </div>

      {/* Commentaire référent */}
      {inventory.referent_comment && (
        <div className="rounded-md bg-white border border-indigo-200 p-3 text-sm">
          <p className="text-[11px] uppercase tracking-wide text-indigo-600 font-semibold mb-1">
            Justification du responsable
          </p>
          <p className="text-zinc-800 whitespace-pre-wrap" data-testid="inv-manager-ref-comment">
            {inventory.referent_comment}
          </p>
        </div>
      )}

      {/* Refus précédent éventuel */}
      {inventory.manager_rejection_comment && (
        <div className="rounded-md bg-red-50 border border-red-200 p-2 text-xs">
          <span className="text-red-800 font-semibold">Refus précédent : </span>
          <span className="text-red-700">{inventory.manager_rejection_comment}</span>
        </div>
      )}

      {canActAsManager ? (
        <>
          {/* Liste éditable des écarts */}
          {ecartLines.length > 0 && (
            <div className="rounded-md border border-indigo-200 bg-white">
              <div className="px-3 py-2 border-b border-indigo-200 text-[11px] uppercase tracking-wide text-indigo-700 font-semibold flex items-center justify-between">
                <span>Lignes en écart · correction possible</span>
                <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 border text-[10px]">
                  {ecartLines.length}
                </Badge>
              </div>
              <ul className="divide-y divide-indigo-100 max-h-[260px] overflow-auto">
                {ecartLines.map((l) => {
                  const isEditing = editingLineId === l.id;
                  return (
                    <li key={l.id} className="px-3 py-2 text-xs" data-testid={`inv-manager-line-${l.id}`}>
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono text-zinc-500">{l.cip || '—'}</span>
                        <span className="font-medium text-zinc-900 flex-1 truncate">{l.designation || '—'}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-zinc-700">
                        <span>Théo : <strong>{l.stock_theorique ?? 0}</strong></span>
                        <span>Compté : <strong>{l.stock_physique ?? '—'}</strong></span>
                        <span className={l.ecart > 0 ? 'text-emerald-700' : 'text-red-700'}>
                          Écart : <strong>{l.ecart > 0 ? '+' : ''}{l.ecart}</strong>
                          {l.valeur_ecart_fcfa ? ` (${fmtFcfa(l.valeur_ecart_fcfa)})` : ''}
                        </span>
                        {!isEditing && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingLineId(l.id);
                              setEditingValue(String(l.stock_physique ?? 0));
                              setEditingComment('');
                            }}
                            className="h-6 px-2 text-indigo-700 hover:bg-indigo-100"
                            data-testid={`inv-manager-line-edit-${l.id}`}
                          >
                            <Pencil className="w-3 h-3 mr-1" />Corriger
                          </Button>
                        )}
                      </div>
                      {isEditing && (
                        <div className="mt-2 flex flex-wrap gap-2 items-end p-2 rounded bg-indigo-50">
                          <label className="text-[11px] text-indigo-800">
                            Nouveau stock physique
                            <input
                              type="number"
                              min="0"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="block mt-0.5 w-24 rounded border border-indigo-300 px-2 py-1 text-sm"
                              data-testid={`inv-manager-line-edit-input-${l.id}`}
                            />
                          </label>
                          <label className="text-[11px] text-indigo-800 flex-1 min-w-[180px]">
                            Note (optionnel)
                            <input
                              type="text"
                              value={editingComment}
                              onChange={(e) => setEditingComment(e.target.value)}
                              maxLength={500}
                              className="block mt-0.5 w-full rounded border border-indigo-300 px-2 py-1 text-sm"
                              placeholder="Source de la correction..."
                            />
                          </label>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              onClick={() => submitLineCorrection(l.id)}
                              disabled={editingLoading}
                              className="bg-indigo-600 hover:bg-indigo-700 text-white"
                              data-testid={`inv-manager-line-save-${l.id}`}
                            >
                              {editingLoading && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                              Enregistrer
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setEditingLineId(null); setEditingValue(''); setEditingComment(''); }}
                              disabled={editingLoading}
                            >
                              Annuler
                            </Button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Décision globale */}
          <div className="rounded-md border border-indigo-200 bg-white p-3">
            <p className="text-[11px] uppercase tracking-wide text-indigo-700 font-semibold mb-2">
              Décision finale
            </p>
            <div className="flex gap-2 mb-2">
              <Button
                size="sm"
                onClick={() => setDecision('approve')}
                variant={decision === 'approve' ? 'default' : 'outline'}
                className={decision === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}
                data-testid="inv-manager-choose-approve"
              >
                <ShieldCheck className="w-3.5 h-3.5 mr-1" />Valider
              </Button>
              <Button
                size="sm"
                onClick={() => setDecision('reject')}
                variant={decision === 'reject' ? 'default' : 'outline'}
                className={decision === 'reject' ? 'bg-red-600 hover:bg-red-700 text-white' : 'border-red-300 text-red-700 hover:bg-red-50'}
                data-testid="inv-manager-choose-reject"
              >
                <ShieldX className="w-3.5 h-3.5 mr-1" />Refuser (retour OP2)
              </Button>
            </div>
            <Textarea
              value={mgrComment}
              onChange={(e) => setMgrComment(e.target.value)}
              placeholder={decision === 'reject'
                ? 'Expliquez pourquoi vous refusez (10 caractères minimum) — l\'inventaire repassera à OP2.'
                : 'Commentaire optionnel pour l\'audit.'}
              maxLength={2000}
              rows={3}
              className="bg-white border-indigo-300"
              data-testid="inv-manager-comment-input"
              disabled={mgrLoading}
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-[11px] text-indigo-700">{mgrComment.length}/2000</span>
              <Button
                size="sm"
                onClick={() => submitManagerValidation(decision)}
                disabled={!decision || mgrLoading || (decision === 'reject' && mgrComment.trim().length < 10)}
                className={
                  decision === 'reject'
                    ? 'bg-red-600 hover:bg-red-700 text-white disabled:opacity-50'
                    : 'bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50'
                }
                data-testid="inv-manager-submit-btn"
              >
                {mgrLoading && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                {decision === 'reject' ? 'Confirmer le refus' : decision === 'approve' ? 'Confirmer la validation' : 'Choisir une décision'}
              </Button>
            </div>
          </div>
        </>
      ) : (
        <div className="text-sm text-indigo-800 italic" data-testid="inv-manager-waiting">
          En attente que le Manager Stock valide ou refuse l'inventaire.
        </div>
      )}
    </div>
  );
};

export default InventoryReferentValidationCard;
